import { createFeature, createReducer, on } from '@ngrx/store';
import { tryGetQuoteIsin } from '@zero/shared/util-helper';
import { EasyTradingMatrix } from '../entities/easy-trading-matrix.entity';
import { Underlying } from '../entities/underlying.entity';
import { tradingToolsActions, tradingToolsApiActions } from './trading-tools.actions';

export interface TradingToolsState {
  loadingUnderlyings: boolean;
  underlyings: Underlying[];
  underlyingsError: string;
  loadingEasyTradingMatrix: boolean;
  easyTradingMatrix: EasyTradingMatrix;
  easyTradingMatrixError: string;
  activeUnderlyingIsin: string;
}

const initialState: TradingToolsState = {
  loadingUnderlyings: false,
  underlyings: [],
  underlyingsError: null,
  loadingEasyTradingMatrix: false,
  easyTradingMatrix: null,
  easyTradingMatrixError: null,
  activeUnderlyingIsin: null,
};

export const tradingToolsFeature = createFeature({
  name: 'tradingTools',
  reducer: createReducer(
    initialState,
    on(
      tradingToolsActions.loadEasyTradingMatrix,
      (state): TradingToolsState => ({
        ...state,
        loadingEasyTradingMatrix: true,
        easyTradingMatrixError: null,
      }),
    ),
    on(
      tradingToolsApiActions.loadEasyTradingMatrixSuccess,
      (state, { matrix }): TradingToolsState => ({
        ...state,
        loadingEasyTradingMatrix: false,
        easyTradingMatrixError: null,
        easyTradingMatrix: matrix,
      }),
    ),
    on(
      tradingToolsApiActions.loadEasyTradingMatrixFailure,
      (state, { error }): TradingToolsState => ({
        ...state,
        loadingEasyTradingMatrix: false,
        easyTradingMatrixError: error,
        easyTradingMatrix: null,
      }),
    ),
    on(
      tradingToolsActions.loadUnderlyings,
      (state): TradingToolsState => ({
        ...state,
        loadingUnderlyings: true,
        underlyingsError: null,
      }),
    ),
    on(
      tradingToolsApiActions.loadUnderlyingsSuccess,
      (state, { underlyings }): TradingToolsState => ({
        ...state,
        underlyingsError: null,
        underlyings,
        activeUnderlyingIsin: underlyings[0]?.isin,
      }),
    ),
    on(
      tradingToolsApiActions.loadUnderlyingsFailure,
      (state, { error }): TradingToolsState => ({
        ...state,
        loadingUnderlyings: false,
        underlyingsError: error,
        underlyings: null,
      }),
    ),
    on(tradingToolsActions.selectUnderlying, (state, { isin }): TradingToolsState => {
      return {
        ...state,
        activeUnderlyingIsin: isin,
      };
    }),
    on(tradingToolsActions.updateUnderlyingQuotes, (state, { quotes, historicQuotes }): TradingToolsState => {
      return {
        ...state,
        loadingUnderlyings: false,
        underlyings: state.underlyings.map((underlying) => {
          const quoteIsin = tryGetQuoteIsin(underlying.isin);
          const quote = quotes[quoteIsin];
          const historicQuote = historicQuotes[quoteIsin]?.[0];

          if (!quote || !historicQuote) {
            return underlying;
          }

          const lastPrice = quote?.bid ?? quote?.ask ?? 0;
          const previousPrice = historicQuote?.bid ?? lastPrice;

          const perfAbs = lastPrice - previousPrice;
          const perfPct = previousPrice !== 0 ? (perfAbs / previousPrice) * 100 : undefined;

          return {
            ...underlying,
            lastPrice,
            perfAbs,
            perfPct,
          };
        }),
      };
    }),
  ),
});
